import { useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { BiBlock } from "react-icons/bi";
import NoPictureWarning from "../resources/nopic.png";
import useFilters from "./reusables/filterBar/hooks/useFilters";
import buildQueryParams from "./reusables/filterBar/hooks/buildQueryParams";
import ClientService from "../rest/clientService";
import CustomerService from "../rest/customerService";
import FilterBar from "./reusables/filterBar/filterBar";
import Pagination from "./lib/pagination";
import { formatCurrency } from "../utils/CurrencyUtils";
import SmartImage from "./reusables/smartImage";

const adjustPageSize = () => {
    if (window.innerWidth >= 1536) return 15;
    if (window.innerWidth >= 1280) return 12;
    if (window.innerWidth >= 1024) return 9;
    if (window.innerWidth >= 768) return 9;
    return 6;
};

const normalizeFilterValue = (item) => {
    if (item === null || item === undefined) return null;
    if (typeof item === "object" && "value" in item) return item.value;
    return item;
};

const CustomerGridSale = ({ onValueChange, parentLogin }) => {
    const [pageSize, setPageSize] = useState(adjustPageSize());
    const [allData, setAllData] = useState([]);
    const [pagedData, setPagedData] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showImages, setShowImages] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [resolvingImages, setResolvingImages] = useState(false);
    const [gradeOptions, setGradeOptions] = useState([]);

    const {
        search,
        setSearch,
        filters,
        updateFilter,
        clearFilters,
        page,
        setPage,
    } = useFilters();

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const prefs = await ClientService.getClientAdminPreferences();
                setShowImages(prefs?.enable_profile_pictures === true);

                if (prefs?.grades) {
                    const gradesArray = prefs.grades
                        .split(";")
                        .map(g => g.trim())
                        .filter(g => g !== "")
                        .map(g => ({ value: g, label: g }));
                    setGradeOptions(gradesArray);
                }
            } catch (error) {
                console.error("Erro config:", error);
            }
        };
        loadPreferences();
    }, []);

    useEffect(() => {
        const onResize = () => setPageSize(adjustPageSize());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const customerFilters = useMemo(() => {
        const configs = [
            {
                key: "type",
                label: "Tipo",
                options: [
                    { value: "student", label: "Aluno" },
                    { value: "employee", label: "Colaborador" },
                ],
                selectedValues: filters.type || [],
                onChange: values => updateFilter("type", values),
                placeholder: "Tipo",
            },
            {
                key: "creditStatus",
                label: "Saldo",
                options: [
                    { value: "positive", label: "Positivo" },
                    { value: "negative", label: "Negativo" },
                    { value: "zero", label: "Zerado" },
                ],
                selectedValues: filters.creditStatus || [],
                onChange: values => {
                    const singleValue = values.slice(-1);
                    updateFilter("creditStatus", singleValue);
                },
                placeholder: "Saldo",
            },
        ];

        const isEmployeeOnly = filters.type?.length === 1 && filters.type[0] === "employee";
        if (gradeOptions.length > 0 && !isEmployeeOnly) {
            configs.splice(1, 0, {
                key: "grades",
                label: "Turma",
                options: gradeOptions,
                selectedValues: filters.grades || [],
                onChange: values => updateFilter("grades", values),
                placeholder: "Turma",
            });
        }
        return configs;
    }, [filters, updateFilter, gradeOptions]);

    const mapFiltersToApi = (currentFilters) => {
        const apiFilters = {};
        apiFilters.userStatus = true;

        if (currentFilters.grades?.length > 0) {
            apiFilters.grades = currentFilters.grades;
        }

        if (currentFilters.creditStatus?.length === 1) {
            const value = normalizeFilterValue(currentFilters.creditStatus[0]);
            if (value === "negative") apiFilters.negativeBalance = true;
            if (value === "positive") {
                apiFilters.negativeBalance = false;
                apiFilters.minCredit = 0.01;
            }
            if (value === "zero") {
                apiFilters.minCredit = 0;
                apiFilters.maxCredit = 0;
            }
        }
        return apiFilters;
    };

    const fetchAllData = async () => {
        try {
            setLoadingData(true);
            if (parentLogin) {
                const students = await CustomerService.findStudentsByParent(parentLogin);
                const studentsTyped = students.map(s => ({ ...s, type: "student" }));
                setAllData(studentsTyped);
                return;
            }

            const selectedTypes = filters.type ? filters.type.map(normalizeFilterValue) : [];
            const fetchStudents = selectedTypes.length === 0 || selectedTypes.includes("student");
            const fetchEmployees = selectedTypes.length === 0 || selectedTypes.includes("employee");
            const apiFilters = mapFiltersToApi(filters);
            const queryParams = buildQueryParams({
                search,
                filters: apiFilters,
                page: 0,
                pageSize: 2000,
            });

            const requests = [];
            const meta = [];

            if (fetchStudents) {
                requests.push(CustomerService.getAllStudents({ ...queryParams, sort: "" }));
                meta.push("student");
            }

            const hasGradeFilter = filters.grades?.length > 0;
            if (fetchEmployees && !hasGradeFilter) {
                requests.push(CustomerService.getAllEmployees({ ...queryParams, sort: "" }));
                meta.push("employee");
            }

            const responses = await Promise.all(requests);
            let mergedList = [];
            responses.forEach((resp, idx) => {
                const type = meta[idx];
                const list = resp.content.map(item => ({ ...item, type }));
                mergedList = [...mergedList, ...list];
            });

            mergedList.sort((a, b) => {
                // Ativos primeiro (user_status !== false)
                const aActive = a.user_status !== false ? 1 : 0;
                const bActive = b.user_status !== false ? 1 : 0;
                if (bActive !== aActive) return bActive - aActive;
                return (a.name || '').localeCompare(b.name || '');
            });
            setAllData(mergedList);
            setPage(0);

        } catch (error) {
            console.error("Erro fetch data:", error);
            setAllData([]);
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(fetchAllData, 300);
        return () => clearTimeout(timeout);
    }, [search, filters, parentLogin]);

    useEffect(() => {
        const processCurrentPage = () => {
            if (allData.length === 0) {
                setPagedData([]);
                return;
            }
            const startIndex = page * pageSize;
            const endIndex = startIndex + pageSize;
            setPagedData(allData.slice(startIndex, endIndex));
        };
        processCurrentPage();
    }, [page, pageSize, allData]);

    const handleCardClick = (item) => {
        const itemId = item.type === "student" ? item.id_student : item.id;
        const itemUniqueKey = `${item.type}-${itemId}`;
        const isCurrentSelected = selectedIndex === itemUniqueKey;
        setSelectedIndex(isCurrentSelected ? null : itemUniqueKey);
        onValueChange(isCurrentSelected ? undefined : item);
    };

    const totalItems = allData.length;
    const computedTotalPages = Math.ceil(totalItems / pageSize);
    const isLoading = loadingData || resolvingImages;

    return (
        <div className="flex-col w-full h-full">
            <div className="flex justify-center my-2 px-2">
                <FilterBar
                    searchValue={search}
                    onSearchChange={value => setSearch(value)}
                    onResetPage={() => setPage(0)}
                    searchPlaceholder="Buscar clientes..."
                    filters={customerFilters}
                    onClearFilters={clearFilters}
                    loading={loadingData}
                />
            </div>

            <div className="flex justify-center my-4 w-full">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-6 w-full px-2 md:px-0">
                    {isLoading && (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 opacity-50">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom_blue mb-4"></div>
                            <p className="text-gray-500 font-medium">Carregando clientes...</p>
                        </div>
                    )}

                    {!isLoading && pagedData.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400">
                            <p>Nenhum cliente encontrado.</p>
                        </div>
                    )}

                    {!isLoading && pagedData.map((item) => {
                        const itemId = item.type === "student" ? item.id_student : item.id;
                        const uniqueKey = `${item.type}-${itemId}`;
                        const isSelected = selectedIndex === uniqueKey;
                        const isInactive = item.userStatus === false;
                        const imageBaseUrl = `/foodz/api/v1/uploads/carregarImagemDePerfil?path=${encodeURIComponent(item.image_url)}`;

                        return (
                            <div
                                key={uniqueKey}
                                onClick={() => handleCardClick(item)}
                                className={`
                                    group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300
                                    aspect-[4/5]
                                    ${isSelected ? "ring-4 ring-custom_blue shadow-xl scale-[1.02]" : "ring-1 ring-slate-200 hover:shadow-lg hover:scale-[1.01]"}
                                    ${isInactive ? "grayscale opacity-80" : ""}
                                `}
                            >
                                <div className="absolute inset-0 w-full h-full">
                                    {showImages && item.image_url ? (
                                        <SmartImage
                                            src={imageBaseUrl}
                                            isThumb={true}
                                            alt={item.name}
                                            className="object-cover"
                                        />
                                    ) : (
                                        <img
                                            src={NoPictureWarning}
                                            className="opacity-40 object-contain p-4 w-full h-full"
                                            alt="Sem imagem"
                                        />
                                    )}
                                </div>

                                {isSelected && (
                                    <div className="absolute top-3 right-3 z-20 bg-white rounded-full p-1 shadow-sm animate-in fade-in zoom-in">
                                        <FaCheckCircle className="text-custom_blue text-xl" />
                                    </div>
                                )}

                                {isInactive && (
                                    <div className="absolute top-3 left-3 z-20 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                                        <BiBlock /> INATIVO
                                    </div>
                                )}

                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90 transition-opacity group-hover:opacity-100" />

                                <div className="absolute bottom-0 left-0 w-full p-3 md:p-4 flex flex-col justify-end text-white z-10">
                                    <h3 className="font-bold text-base md:text-lg leading-tight line-clamp-2 drop-shadow-md break-words">
                                        {item.name}
                                    </h3>
                                    <p className="text-gray-300 text-xs mt-1 font-medium">
                                        {item.type === "student" ? `Turma ${item.grade || 'N/A'}` : "Colaborador"}
                                    </p>

                                    <div className="mt-2 md:mt-3 flex items-center gap-2 md:gap-3 border-t border-white/20 pt-2">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Saldo</span>
                                            <span className={`font-semibold text-xs ${item.credit > 0 ? "text-green-400" : item.credit < 0 ? "text-red-400" : "text-gray-100"}`}>
                                                {formatCurrency(item.credit)}
                                            </span>
                                        </div>

                                        {item.emergency_value > 0 && (
                                            <div className="flex flex-col pl-2 md:pl-3 border-l border-white/20">
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Emerg.</span>
                                                <span className="font-semibold text-xs text-yellow-400">
                                                    {formatCurrency(item.emergency_value)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {computedTotalPages > 1 && (
                <div className="flex justify-center my-4 pb-10">
                    <Pagination
                        currentPage={page}
                        totalPages={computedTotalPages}
                        onPageChange={setPage}
                        totalItems={totalItems}
                        pageSize={pageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default CustomerGridSale;